import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { orderBy, QueryCompositeFilterConstraint } from "firebase/firestore";
import { FirestoreService } from "../../../../shared/api/firebase/firestoreService";
import { FirestoreCollectionReference } from "../../../../shared/api/firebase/references";
import { MedicalPaOrder } from "../../../data-model/medicalPaOrdersModel";
import { MedicalPaOrdersAuthStatus } from "../../../enums/medicalPaOrdersAuthStatus";
import { createCompositeFilterForMedicalPaTables } from "../../../utils/createCompositeFilterForTables";
import { logDataToConsole } from "../../../utils/customLogger";

interface MedicalPaOrdersState {
  pendingMedicalPaOrders: MedicalPaOrder[] | null;
  loading: boolean;
  error: string | null;
  lastVisible: any;
  showinLineLoader: boolean;
  totalDocuments: number;
  currentlyLoadedCount: number;
}

const initialState: MedicalPaOrdersState = {
  pendingMedicalPaOrders: null,
  loading: false,
  error: null,
  lastVisible: null,
  showinLineLoader: false,
  totalDocuments: 0,
  currentlyLoadedCount: 0,
};

const pendingMedicalPaOrderSlice = createSlice({
  name: "pendingMedicalPaOrders",
  initialState,
  reducers: {
    setPendingMedicalPaOrders: (
      state,
      action: PayloadAction<MedicalPaOrder[]>,
    ) => {
      state.pendingMedicalPaOrders = action.payload;
      state.currentlyLoadedCount = action.payload.length;
      state.loading = false;
      state.error = null;
    },
    appendPendingMedicalPaOrders: (
      state,
      action: PayloadAction<MedicalPaOrder[]>,
    ) => {
      state.pendingMedicalPaOrders = state.pendingMedicalPaOrders
        ? [...state.pendingMedicalPaOrders, ...action.payload]
        : action.payload;
      state.currentlyLoadedCount = state.pendingMedicalPaOrders.length;
      state.loading = false;
      state.error = null;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
      if (action.payload) {
        state.error = null;
      }
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.pendingMedicalPaOrders = null;
      state.loading = false;
    },
    setLastVisible: (state, action: PayloadAction<any>) => {
      state.lastVisible = action.payload;
    },
    setTotalDocuments: (state, action: PayloadAction<number>) => {
      state.totalDocuments = action.payload;
    },
    resetPagination: (state) => {
      state.totalDocuments = 0;
      state.lastVisible = null;
      state.currentlyLoadedCount = 0;
      state.pendingMedicalPaOrders = null;
    },
    setinLineLoader: (state, action: PayloadAction<boolean>) => {
      state.showinLineLoader = action.payload;
    },
  },
});

export const {
  setPendingMedicalPaOrders,
  appendPendingMedicalPaOrders,
  setLoading,
  setError,
  setLastVisible,
  setTotalDocuments,
  resetPagination,
  setinLineLoader,
} = pendingMedicalPaOrderSlice.actions;

export const fetchTotalDocumentsCount =
  (
    facilityId: string,
    providerId: string,
    searchTerm?: string,
    isAdmin?: boolean,
  ) =>
  async (dispatch) => {
    try {
      const allDocs =
        await FirestoreService.getAllDocumentCountByCompositeFilter(
          FirestoreCollectionReference.medicalPaOrders(),
          createCompositeFilterForMedicalPaTables(
            facilityId,
            providerId,
            MedicalPaOrdersAuthStatus.Pending,
            searchTerm,
            isAdmin,
          ) as QueryCompositeFilterConstraint,
        );

      dispatch(setTotalDocuments(allDocs));
    } catch (error) {
      logDataToConsole("Error fetching total document count", error);
      dispatch(setTotalDocuments(0));
      return 0;
    }
  };

export const fetchPendingMedicalPaOrdersAfterSearch =
  (
    facilityId: string,
    providerId: string,
    searchTerm?: string,
    isAdmin?: boolean,
  ) =>
  async (dispatch, getState) => {
    const { pendingMedicalPaOrders } = getState();
    if (pendingMedicalPaOrders.loading) return;

    dispatch(setLoading(true));
    dispatch(resetPagination());

    try {
      const data =
        await FirestoreService.fetchQueryCollectionByCompositeFilter<MedicalPaOrder>(
          FirestoreCollectionReference.medicalPaOrders(),
          createCompositeFilterForMedicalPaTables(
            facilityId,
            providerId,
            MedicalPaOrdersAuthStatus.Pending,
            searchTerm,
            isAdmin,
          ) as QueryCompositeFilterConstraint,
          [orderBy("created_at", "desc")],
        );

      dispatch(setPendingMedicalPaOrders(data as MedicalPaOrder[]));
      dispatch(
        fetchTotalDocumentsCount(
          facilityId,
          providerId,
          searchTerm,
          isAdmin ?? false,
        ),
      );
    } catch (error) {
      dispatch(setError(error as string));
    }
  };

export const fetchPendingMedicalPaOrders =
  (
    facilityId: string,
    pageSize: number,
    providerId: string,
    isAdmin: boolean,
  ) =>
  async (dispatch, getState) => {
    const { pendingMedicalPaOrders } = getState();
    if (pendingMedicalPaOrders.loading) return;

    dispatch(setLoading(true));
    dispatch(resetPagination());

    try {
      FirestoreService.paginateQueryWithCompositeFilter<MedicalPaOrder>(
        FirestoreCollectionReference.medicalPaOrders(),
        pageSize,
        ({ docs, lastVisible }) => {
          dispatch(setPendingMedicalPaOrders(docs));
          dispatch(setLastVisible(lastVisible));
          dispatch(
            fetchTotalDocumentsCount(
              facilityId,
              providerId,
              undefined,
              isAdmin,
            ),
          );
        },
        (error) => {
          dispatch(setError(error.message));
        },
        undefined,
        createCompositeFilterForMedicalPaTables(
          facilityId,
          providerId,
          MedicalPaOrdersAuthStatus.Pending,
          undefined,
          isAdmin,
        ) as QueryCompositeFilterConstraint,
        [orderBy("created_at", "desc")],
      );
    } catch (error) {
      logDataToConsole("Error in fetchPendingMedicalPaOrders", error);
      dispatch(setError(error as string));
    }
  };

export const fetchMorePendingMedicalPaOrders =
  (
    facilityId: string,
    pageSize: number,
    providerId?: string,
    isAdmin?: boolean,
  ) =>
  async (dispatch, getState) => {
    const { pendingMedicalPaOrders } = getState();

    if (
      pendingMedicalPaOrders.loading ||
      pendingMedicalPaOrders.showinLineLoader ||
      pendingMedicalPaOrders.currentlyLoadedCount ===
        pendingMedicalPaOrders.totalDocuments
    ) {
      return;
    }

    dispatch(setinLineLoader(true));

    try {
      const unsubscribe =
        FirestoreService.paginateQueryWithCompositeFilter<MedicalPaOrder>(
          FirestoreCollectionReference.medicalPaOrders(),
          pageSize,
          ({ docs, lastVisible }) => {
            dispatch(appendPendingMedicalPaOrders(docs));
            dispatch(setLastVisible(lastVisible));
            dispatch(setinLineLoader(false));
          },
          (error) => {
            dispatch(setError(error.message));
            dispatch(setinLineLoader(false));
          },
          pendingMedicalPaOrders.lastVisible,
          createCompositeFilterForMedicalPaTables(
            facilityId,
            providerId ?? "",
            MedicalPaOrdersAuthStatus.Pending,
            undefined,
            isAdmin,
          ) as QueryCompositeFilterConstraint,
          [orderBy("created_at", "desc")],
        );

      return unsubscribe;
    } catch (error) {
      dispatch(setError(error as string));
      dispatch(setinLineLoader(false));
    }
  };

export const hasMoreDataToLoad = (state, endIndex: number) => {
  const { pendingMedicalPaOrders } = state;

  return (
    pendingMedicalPaOrders.currentlyLoadedCount <
      pendingMedicalPaOrders.totalDocuments &&
    endIndex === pendingMedicalPaOrders.currentlyLoadedCount - 1
  );
};

export default pendingMedicalPaOrderSlice.reducer;
